/**
 * Mirror of the server's PasswordPolicy
 * (crm-portal-api/app/models/concerns/password_policy.rb).
 *
 * The server is the one that decides — this exists so somebody is told what is
 * wrong while they type instead of after a round trip that spends their reset
 * token. Keep the two in step; the wording is deliberately identical.
 */
export const MIN_LENGTH = 10;
export const MAX_LENGTH = 128;
export const REQUIRED_CLASSES = 3;

// Below this an identity term is too short to mean anything.
const MIN_IDENTITY_TERM = 4;

export const CHARACTER_CLASSES = [
  { label: 'a lowercase letter', pattern: /[a-z]/ },
  { label: 'an uppercase letter', pattern: /[A-Z]/ },
  { label: 'a number', pattern: /\d/ },
  { label: 'a symbol', pattern: /[^A-Za-z0-9]/ }
];

export const characterClassesUsed = (password = '') =>
  CHARACTER_CLASSES.filter(({ pattern }) => pattern.test(password)).length;

const identityTerms = ({ email, name }) =>
  [String(email || '').split('@')[0], name]
    .map((term) => String(term || '').toLowerCase().trim())
    .filter((term) => term.length >= MIN_IDENTITY_TERM);

/** The reasons `password` is not acceptable; empty when it is. */
export const passwordPolicyErrors = (password = '', { email, name } = {}) => {
  const value = String(password || '');
  const errors = [];

  if (value.length < MIN_LENGTH) errors.push(`must be at least ${MIN_LENGTH} characters`);
  if (value.length > MAX_LENGTH) errors.push(`must be at most ${MAX_LENGTH} characters`);

  if (characterClassesUsed(value) < REQUIRED_CLASSES) {
    errors.push(
      `must contain at least ${REQUIRED_CLASSES} of: ${CHARACTER_CLASSES.map((c) => c.label).join(', ')}`
    );
  }

  const haystack = value.toLowerCase();
  if (haystack && identityTerms({ email, name }).some((term) => haystack.includes(term))) {
    errors.push('must not contain your name or email address');
  }

  return errors;
};

/**
 * A coarse 0–4 score for the meter. Deliberately not a crack-time estimate:
 * that needs a dictionary (zxcvbn) and this is here to nudge, while
 * passwordPolicyErrors is what actually gates submission.
 */
export const passwordStrength = (password = '') => {
  const value = String(password || '');
  if (!value) return 0;

  let score = characterClassesUsed(value);
  if (value.length >= MIN_LENGTH + 4) score += 1;
  if (value.length < MIN_LENGTH) score = Math.min(score, 1);

  return Math.max(0, Math.min(4, score));
};

export const STRENGTH_LABELS = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
